import React, { useState, useEffect } from 'react';
import { apiRequest } from '../../utils/apiUtils';
import { useUI } from '../../contexts/UIContext';
import QueryLogsTable from '../../components/admin/QueryLogsTable';
import QueryStatsCards from '../../components/admin/QueryStatsCards';

/**
 * Query History Page
 *
 * Admin page for viewing comprehensive query logs with filters.
 * Shows all SQL queries executed by Sparvi against customer databases.
 */
const QueryHistoryPage = () => {
  const { showNotification } = useUI();

  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);

  // Filters
  const [dateRange, setDateRange] = useState(7); // days
  const [selectedConnection, setSelectedConnection] = useState('');
  const [selectedAutomationType, setSelectedAutomationType] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedQueryType, setSelectedQueryType] = useState('');

  // Pagination
  const [limit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Available connections (for filter dropdown)
  const [connections, setConnections] = useState([]);

  useEffect(() => {
    loadConnections();
  }, []);

  useEffect(() => {
    loadLogs();
    loadStats();
  }, [dateRange, selectedConnection, selectedAutomationType, selectedStatus, selectedQueryType, offset]);

  const loadConnections = async () => {
    try {
      // Fetch available connections for filter dropdown
      const response = await apiRequest('/connections', { skipThrottle: true });
      if (response?.data) {
        setConnections(response.data);
      }
    } catch (error) {
      console.error('Error loading connections:', error);
    }
  };

  const loadLogs = async () => {
    try {
      setLoading(true);

      const params = {
        limit,
        offset
      };

      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - dateRange);

      params.start_date = startDate.toISOString();
      params.end_date = endDate.toISOString();

      // Add filters if selected
      if (selectedConnection) params.connection_id = selectedConnection;
      if (selectedAutomationType) params.automation_type = selectedAutomationType;
      if (selectedStatus) params.status = selectedStatus;
      if (selectedQueryType) params.query_type = selectedQueryType;

      const response = await apiRequest('/admin/query-logs', {
        params,
        skipThrottle: true
      });

      if (response?.data) {
        setLogs(response.data);
        setHasMore(response.data.length === limit);
      } else {
        setLogs([]);
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading query logs:', error);
      showNotification('Failed to load query logs', 'error');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      setStatsLoading(true);

      const params = {};

      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - dateRange);

      params.start_date = startDate.toISOString();
      params.end_date = endDate.toISOString();

      // Add connection filter if selected
      if (selectedConnection) params.connection_id = selectedConnection;

      const response = await apiRequest('/admin/query-logs/stats', {
        params,
        skipThrottle: true
      });

      if (response?.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error loading query stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleDateRangeChange = (days) => {
    setDateRange(days);
    setOffset(0); // Reset pagination
  };

  const handleFilterChange = () => {
    setOffset(0); // Reset pagination when filters change
  };

  const handleNextPage = () => {
    if (hasMore) {
      setOffset(offset + limit);
    }
  };

  const handlePrevPage = () => {
    if (offset > 0) {
      setOffset(Math.max(0, offset - limit));
    }
  };

  const handleClearFilters = () => {
    setSelectedConnection('');
    setSelectedAutomationType('');
    setSelectedStatus('');
    setSelectedQueryType('');
    setOffset(0);
  };

  const hasActiveFilters =
    selectedConnection || selectedAutomationType || selectedStatus || selectedQueryType;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Query History</h2>
        <p className="text-sm text-gray-600 mt-1">
          View all SQL queries executed by Sparvi against your data warehouses.
        </p>
      </div>

      {/* Statistics Cards */}
      <QueryStatsCards stats={stats} loading={statsLoading} variant="general" />

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Date Range Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date Range
            </label>
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {[7, 30, 90].map((days) => (
                <button
                  key={days}
                  onClick={() => handleDateRangeChange(days)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    dateRange === days
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {days} Days
                </button>
              ))}
            </div>
          </div>

          {/* Connection Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Connection
            </label>
            <select
              value={selectedConnection}
              onChange={(e) => {
                setSelectedConnection(e.target.value);
                handleFilterChange();
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Connections</option>
              {connections.map((conn) => (
                <option key={conn.id} value={conn.id}>
                  {conn.name}
                </option>
              ))}
            </select>
          </div>

          {/* Automation Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Automation Type
            </label>
            <select
              value={selectedAutomationType}
              onChange={(e) => {
                setSelectedAutomationType(e.target.value);
                handleFilterChange();
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="metadata_collection">Metadata Collection</option>
              <option value="validation">Validation</option>
              <option value="profiling">Profiling</option>
              <option value="anomaly_detection">Anomaly Detection</option>
              <option value="schema_change">Schema Change</option>
              <option value="manual">Manual</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                handleFilterChange();
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="timeout">Timeout</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Query Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Query Type
            </label>
            <select
              value={selectedQueryType}
              onChange={(e) => {
                setSelectedQueryType(e.target.value);
                handleFilterChange();
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Query Types</option>
              <option value="SELECT">SELECT</option>
              <option value="INSERT">INSERT</option>
              <option value="UPDATE">UPDATE</option>
              <option value="DELETE">DELETE</option>
              <option value="CREATE">CREATE</option>
              <option value="ALTER">ALTER</option>
              <option value="DROP">DROP</option>
              <option value="SHOW">SHOW</option>
              <option value="DESCRIBE">DESCRIBE</option>
            </select>
          </div>
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <div className="mt-4">
            <button
              onClick={handleClearFilters}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>

      {/* Query Logs Table */}
      <QueryLogsTable logs={logs} loading={loading} />

      {/* Pagination */}
      {!loading && logs.length > 0 && (
        <div className="flex items-center justify-between bg-white px-4 py-3 border border-gray-200 rounded-lg">
          <div className="text-sm text-gray-700">
            Showing <span className="font-medium">{offset + 1}</span> to{' '}
            <span className="font-medium">{offset + logs.length}</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handlePrevPage}
              disabled={offset === 0}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={handleNextPage}
              disabled={!hasMore}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QueryHistoryPage;
